/** @fileoverview Layout and wording rules for inputs inside a form: width, required marks, choice tiles and error text. */

import type { FabrikInputComponent } from '../../types/apps';

const CHOICE_TYPES = new Set(['select', 'radio', 'lookup']);
/* Most options, and the longest label, that still read well as tiles in a grid. */
const MAX_TILE_OPTIONS = 6;
const MAX_TILE_LABEL = 24;
/* Most fixed options a select shows as a segmented control; past this it stays a dropdown. */
const MAX_SEGMENTED_SELECT_OPTIONS = 6;

/**
 * Tells whether a choice input shows as one joined segmented control: a radio asked to, or a
 * select asked to whose options are fixed and few. Any other select stays a dropdown.
 */
export const showsSegmented = ({ inputType, presentation, options }: FabrikInputComponent) =>
  presentation === 'segmented' &&
  (inputType === 'radio' ||
    (inputType === 'select' &&
      Boolean(
        options && 'items' in options && options.items.length <= MAX_SEGMENTED_SELECT_OPTIONS
      )));

/** Tells whether a required segmented control still needs a selection. */
export const isMissingRequiredSegmented = (input: FabrikInputComponent, value: unknown) =>
  showsSegmented(input) && Boolean(input.required) && value === undefined;

/** Tells whether a form marks its optional inputs instead of its required ones: true when at least half are required. */
export const marksOptionalInputs = (inputs: FabrikInputComponent[]) =>
  inputs.filter(({ required }) => required).length * 2 >= inputs.length;

/** Tells whether radio options show as tiles in a grid: a few options with short labels. */
export const showsChoiceTiles = (options: { label: string }[]) =>
  options.length <= MAX_TILE_OPTIONS &&
  options.every(({ label }) => label.length <= MAX_TILE_LABEL);

/** Words why a control is not valid, for the line under its field. */
export const readInvalidText = (
  input: FabrikInputComponent,
  element: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
) => {
  /* The browser's pattern message names no format; the input's help text does. */
  if (element.validity.patternMismatch && input.description) {
    return `Use this format: ${input.description}.`;
  }
  if (!element.validity.valueMissing) {
    return element.validationMessage;
  }
  if (input.inputType === 'checkbox' || input.inputType === 'switch') {
    return `Turn on ${input.label} to continue.`;
  }
  return CHOICE_TYPES.has(input.inputType)
    ? `Choose a value for ${input.label}.`
    : `${input.label} is required.`;
};
